"use client";
import { useEffect, useRef, useMemo, useState } from "react";

const CameraSlider = ({ id, height = "400px", config = {} }) => {
  const cameraRef = useRef(null);
  const [sliderHeight, setSliderHeight] = useState(height);
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch slider data dynamically from API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_POST_URL}/api/slider/${id}`
        );
        if (!res.ok) throw new Error("Failed to fetch slider");
        const data = await res.json();

        // Parse stringified array safely
        let parsedImages = [];
        try {
          parsedImages = JSON.parse(data?.data?.sd_images || "[]");
        } catch (e) {
          console.error("Error parsing sd_images:", e);
        }

        setSlides(parsedImages);
      } catch (err) {
        console.error("Error fetching slider:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSlides();
  }, [id]);

  // Responsive height
  useEffect(() => {
    const updateHeight = () => {
      setSliderHeight(window.innerWidth <= 768 ? "250px" : "400px");
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const uniqueId = useMemo(
    () => `camera_wrap_${Math.random().toString(36).substr(2, 9)}`,
    []
  );

  const defaultConfig = {
    height: sliderHeight,
    time: 5000,
    transPeriod: 1000,
    thumbnails: true,
    pagination: true,
    fx: "curtainTopLeft",
    hover: true,
    playPause: false,
    autoAdvance: true,
    mobileAutoAdvance: false,
    ...config,
  };

  // Initialize Camera plugin
  useEffect(() => {
    if (slides.length === 0) return;

    if (window.jQuery && window.jQuery.fn.camera && cameraRef.current) {
      const timer = setTimeout(() => {
        const $element = window.jQuery(cameraRef.current);
        if ($element.data("camera")) {
          $element.data("camera", null);
        }
        $element.camera(defaultConfig);
      }, 100);

      return () => {
        clearTimeout(timer);
        if (window.jQuery && cameraRef.current) {
          const $element = window.jQuery(cameraRef.current);
          if ($element.data("camera")) {
            try {
              $element.trigger("camera_stop");
              $element.data("camera", null);
              $element.removeClass("camera_wrap_processed");
            } catch (e) {
              console.warn("Error cleaning up camera instance:", e);
            }
          }
        }
      };
    }
  }, [slides, uniqueId]);

  if (isLoading) {
    return <div className="text-center">Loading slider...</div>;
  }

  if (slides.length === 0) {
    return <div className="text-center">No slides available</div>;
  }

  return (
    <div className="w-full">
      <div
        className="camera_wrap camera_azure_skin w-full"
        id={uniqueId}
        ref={cameraRef}
        style={{ width: "100%" }}
      >
        {slides.map((slide, index) => (
          <div
            key={`slide-${index}`}
            data-thumb={`${process.env.NEXT_PUBLIC_POST_URL}/api/uploads/${slide}`}
            data-src={`${process.env.NEXT_PUBLIC_POST_URL}/api/uploads/${slide}`}
            data-alt={`Slide ${index + 1}`}
          ></div>
        ))}
      </div>
    </div>
  );
};

export default CameraSlider;
